import apiV2Service from '../api/api-v2.service';
import {DigitalAsset, RemoteDigitalAsset} from './digitalAssersManager.types';
import {DateTime} from 'luxon';
import {JobExecutionParams} from '../jobs/jobs.types';
import {fileUploadApi} from '../fileUpload/fileUpload.api';
import {backgroundProcessService} from '../backgroundProcess/backgroundProcess.service';
import {AxiosProgressEvent} from 'axios';
import parseFileName from 'common/src/utils/fileUtils/parseFileName';
import {ACCEPTED_TYPES} from 'common/src/components/DigitalAssetsTable/components/DAMCreate/DAMCreate';
import waitForJobWithoutBGProcess from '../jobs/utils/waitForJobWithoutBGP';

const BASE_URL = 'DigitalAssetsManager';

function upload(files: File[], folder = '/') {
  const process = backgroundProcessService.startProcess(
    `Upload ${files.length === 1 ? 'Asset' : `${files.length} Assets`}`,
  );

  if (files.length === 1) {
    process.setSubtitle(files[0].name);
  }

  const totalSize = files.reduce((a, b) => a + b.size, 0);
  const loadedPerFile: Record<string, number> = Object.fromEntries(
    files.map((file) => [file.name, 0]),
  );

  return Promise.all(
    files.map(async (file) => {
      const key = folder + (file.webkitRelativePath || file.name);
      const {fileExtension} = parseFileName(file.name);
      const fileType =
        file.type || (fileExtension ? ACCEPTED_TYPES[fileExtension] : 'application/octet-stream');

      const fileUrl = await uploadAssetToS3({
        file,
        fileType,
        onUploadProgress: (e) => {
          loadedPerFile[file.name] = e.loaded;
          const sumLoaded = Object.values(loadedPerFile).reduce((a, b) => a + b, 0);
          const percentComplete = Math.min(Math.round((sumLoaded * 100) / totalSize), 95);
          process.progress(percentComplete);
        },
      });

      const {data} = await apiV2Service.executePutRequest<JobExecutionParams>(
        `${BASE_URL}/AssetJob`,
        {key, fileUrl, contentType: fileType},
        {},
      );

      await waitForJobWithoutBGProcess({
        jobExecutionId: data.executionId,
      });
    }),
  )
    .then(() => {
      process.progress(100);
      process.success();
    })
    .catch((e) => {
      process.fail(e);
    });
}

async function uploadAssetToS3({
  file,
  fileType,
  onUploadProgress,
}: {
  file: File;
  fileType: string;
  onUploadProgress?: (e: AxiosProgressEvent) => void;
}): Promise<string> {
  const {presignedUrl, finalS3Url} = await fileUploadApi.getPresignedUrlForUpload(
    fileType,
    file.name,
  );

  await fileUploadApi.uploadFileToS3({
    url: presignedUrl,
    file,
    contentType: fileType,
    onUploadProgress,
  });

  return finalS3Url;
}

function remove(fileKey: string) {
  return apiV2Service.executeDeleteRequest(`${BASE_URL}/Asset`, {key: fileKey});
}

function removeMultiple(keys: string[]) {
  return apiV2Service.executeDeleteRequest(`${BASE_URL}/DeleteMultipleAssets`, {}, {keys: keys});
}

function setMetaData(key: string, tags: Record<string, string>) {
  return apiV2Service.executePutRequest(`${BASE_URL}/AssetMetadata`, {key}, {tags});
}

function getMetaData(fileKey: string): Promise<string> {
  return apiV2Service
    .executeGetRequest<{URL: string}>(`${BASE_URL}/AssetMetadata`, {key: fileKey})
    .then((res) => res.data.URL);
}

function createFolder(folderName: string, parentFolder = '') {
  return apiV2Service.executePutRequest(`${BASE_URL}/Folder`, {
    folderPath: `${parentFolder}${folderName}`,
  });
}

function deleteFolder(folderPath: string) {
  return apiV2Service.executeDeleteRequest(`${BASE_URL}/Folder`, {folderPath, onlyIfEmpty: false});
}

function getSubFoldersList(parentFolder: string): Promise<string[]> {
  return apiV2Service
    .executeGetRequest<{subfolders: string[]}>(`${BASE_URL}/SubFolders`, {folderPath: parentFolder})
    .then((res) => res.data.subfolders);
}

function getAssets(parentFolder = '/'): Promise<DigitalAsset[]> {
  return apiV2Service
    .executeGetRequest<{assets: DigitalAsset[]}>(`${BASE_URL}/AssetList`, {
      folderPath: parentFolder,
      topLevelOnly: true,
    })
    .then(({data}) => data.assets);
}

function getAll(includeMetadata = false): Promise<DigitalAsset[]> {
  return apiV2Service
    .executeGetRequest<{assets: RemoteDigitalAsset[]}>(`${BASE_URL}/All`, {includeMetadata})
    .then((res) =>
      res.data.assets.map((asset) => ({
        ...asset,
        relatedFiles: [],
        lastModifiedDate: DateTime.fromISO(asset.lastModifiedDate || ''),
      })),
    );
}

function copyTo(fileKey: string, destination: string) {
  return apiV2Service.executePostRequest(
    `${BASE_URL}/CopyAsset`,
    {failIfExist: true},
    {key: fileKey, destinationKey: destination + fileKey.replace(/^.*\/([^\/]+$)/, '$1')},
  );
}

function moveTo(fileKey: string, destination: string) {
  return apiV2Service.executePostRequest(
    `${BASE_URL}/MoveAsset`,
    {failIfExist: true},
    {key: fileKey, destinationKey: destination + fileKey.replace(/^.*\/([^\/]+$)/, '$1')},
  );
}

async function downloadAssets(files: string[], folders: string[]) {
  const {data} = await apiV2Service.executePostRequest<JobExecutionParams>(
    `${BASE_URL}/Download`,
    {},
    {
      files: files,
      folders: folders,
    },
  );

  return data;
}

const digitalAssetsApi = {
  upload,
  remove,
  removeMultiple,
  getAssets,
  createFolder,
  deleteFolder,
  getSubFoldersList,
  getMetaData,
  setMetaData,
  copyTo,
  moveTo,
  getAll,
  downloadAssets,
};

export default digitalAssetsApi;
